import Link from 'next/link'
import Image from 'next/image'
import {
  RiFacebookCircleFill,
  RiTwitterXFill,
  RiLinkedinFill,
  RiGithubFill
} from '@remixicon/react'

const navigation = {
  product: [
    { name: 'Features', href: '/product' },
    { name: 'Templates', href: '/templates' },
    { name: 'Integrations', href: '/integrations' },
    { name: 'Pricing', href: '/pricing' },
    {
      name: "What's new ✨",
      href: 'https://github.com/heyform/heyform/releases'
    }
  ],
  resources: [
    { name: 'Help Center', href: 'https://docs.heyform.net' },
    { name: 'Open Source', href: 'https://github.com/heyform/heyform' },
    { name: 'Community', href: 'https://discord.gg/sgT4v4GSTe' },
    { name: 'Blog', href: '/blog' }
  ],
  company: [
    { name: 'About', href: '/about-us' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Partners', href: '/partners' },
    { name: 'Climate Pledge', href: 'https://climate.stripe.com/Vxe5z7' }
  ],
  legal: [
    {
      name: 'Privacy Privacy',
      href: 'https://docs.heyform.net/legal/privacy'
    },
    {
      name: 'Terms of Service',
      href: 'https://docs.heyform.net/legal/tos'
    },
    {
      name: 'Report Abuse',
      href: 'https://docs.heyform.net/legal/report-abuse'
    }
  ],
  compare: [
    {
      name: 'Typeform Alternative',
      href: 'https://heyform.net/blog/vs-typeform'
    },
    {
      name: 'Google Forms Alternative',
      href: 'https://heyform.net/blog/vs-google-forms'
    }
  ],
  social: [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/HeyformHQ/',
      icon: props => <RiFacebookCircleFill {...props} />
    },
    {
      name: 'Twitter',
      href: 'https://twitter.com/HeyformHQ',
      icon: props => <RiTwitterXFill {...props} />
    },
    {
      name: 'Linkedin',
      href: 'https://www.linkedin.com/company/heyform/',
      icon: props => <RiLinkedinFill {...props} />
    },
    {
      name: 'Github',
      href: 'https://github.com/heyform/heyform',
      icon: props => <RiGithubFill {...props} />
    }
  ]
}

function FooterColumn({ title, links }) {
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-secondary">
        {title}
      </h3>
      <ul className="mt-4 space-y-3">
        {links.map(item => (
          <li key={item.name}>
            <a
              href={item.href}
              className="text-body text-primary hover:underline"
            >
              {item.name}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Footer() {
  return (
    <footer
      aria-labelledby="footer-heading"
      className="border-t border-input bg-background"
    >
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>

      <div className="mx-auto w-full max-w-[72rem] px-4 py-24 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-[1.2fr,2fr]">
          <div className="space-y-6">
            <div className="grid justify-start gap-4">
              <a
                href="https://www.producthunt.com/products/heyform"
                target="_blank"
                rel="noreferrer"
                className="flex h-[55px] w-[250px] items-center overflow-hidden"
              >
                <Image
                  src="/static/weekly1.svg"
                  alt="heyform"
                  width={250}
                  height={55}
                  className="h-[55px] w-[250px] scale-[1.18] object-contain"
                />
              </a>
              <a
                href="https://trendshift.io/repositories/9025"
                target="_blank"
                rel="noreferrer"
                className="flex h-[55px] w-[250px] items-center"
              >
                <Image
                  src="/static/tod.svg"
                  alt="heyform"
                  width={250}
                  height={55}
                  className="h-[55px] w-[250px] object-contain"
                />
              </a>
              <a
                href="https://trendshift.io/repositories/9025"
                target="_blank"
                rel="noreferrer"
                className="flex h-[55px] w-[250px] items-center"
              >
                <img
                  src="https://hackernews-badge.vercel.app/api?id=39895960"
                  alt="heyform"
                  width={250}
                  height={55}
                  className="h-[55px] w-[250px] object-contain"
                />
              </a>
            </div>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <FooterColumn title="Product" links={navigation.product} />
            <FooterColumn title="Resources" links={navigation.resources} />
            <FooterColumn title="Company" links={navigation.company} />
            <div className="space-y-8">
              <FooterColumn title="Legal" links={navigation.legal} />
              <FooterColumn title="Compare" links={navigation.compare} />
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-8 border-t border-input pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex items-center">
              <div className="relative rounded-full border-2 border-white">
                <Image
                  src="/static/team-1.jpg"
                  alt="Avatar for Luo"
                  width={32}
                  height={32}
                  className="rounded-full"
                />
              </div>
              <div className="relative -ml-2.5 rounded-full border-2 border-white">
                <Image
                  src="/static/team-3.jpg"
                  alt="Avatar for Wu Qin"
                  width={32}
                  height={32}
                  className="rounded-full"
                />
              </div>
            </div>
            <p className="max-w-md text-body text-secondary">
              <b className="font-semibold text-primary">
                Made by{' '}
                <Link className="underline" href="https://earlybird.im">
                  EarlyBird
                </Link>
              </b>
              , a bootstrapped SaaS company. <br />
              brought to you by{' '}
              <a
                href="https://x.com/LuoBaishun"
                className="font-medium underline text-primary"
              >
                Luo Baishun
              </a>{' '}
              &amp;{' '}
              <a
                href="https://x.com/mufeng_me"
                className="font-medium underline text-primary"
              >
                Wu Qin
              </a>
              .
            </p>
          </div>

          <div className="flex space-x-5">
            {navigation.social.map(item => (
              <a
                key={item.name}
                href={item.href}
                className="text-primary"
                aria-label={item.name}
              >
                <item.icon className="h-6 w-6" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}
